import { useQuery } from "@apollo/client";
import { EligibilityConditionCode, FlexFinancialDataPointSnapshot } from "@lyfegen-platform/graphql-schema/models";
import { EllipsisTypography, Loading, Typography } from "@lyfegen-platform/react-common";
import { distinctBy, getTreatmentCodeByCountry, parseDate } from "@lyfegen-platform/utils";
import { Theme, Tooltip, useTheme, withStyles } from "@material-ui/core";
import { ErrorOutline, InfoOutlined } from "@material-ui/icons";
import { format } from "date-fns";
import i18next from "i18next";
import { memo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ScenarioCondition, Treatment } from "../../../models/generated/graphql";
import { LiteBox } from "../../../components/lite/LiteBox";
import {
	formatDisease,
	formatEligibility,
	formatICondition,
	getPreparations,
	indicationDisplay,
	notAvailableKey,
} from "../../../utils/base-agreement-formatters";
import { tryDeserialize } from "../../../utils/try-deserialize";
import { resolveContentForTooltip } from "../../agreements/agreement/details-cards/Scenarios";
import { GetReviewInvoiceQuery } from "../documents/invoices/types/queries.generated";
import { GET_REFUND_SOURCE_CONDITIONS } from "./queries";
import { GetRefundSourceConditionsQuery } from "./types/queries.generated";

export const ConditionsTooltip = withStyles((theme: Theme) => ({
	tooltip: {
		backgroundColor: (theme.palette.toolTipBg as any)?.main || "black",
		color: theme.palette.common.white,
		maxWidth: 500,
	},
}))(Tooltip);

const Condition = (props: { condition: string; values?: any[]; isSanitized?: boolean }) => {
	const values = Array.from(new Set((props.values || []).filter(Boolean)));

	return (
		<tr>
			<td style={{ maxWidth: "215px", verticalAlign: "top" }}>
				<EllipsisTypography text={props.condition} truncate variant="body2" />
			</td>
			<td>
				<Typography variant="body2" style={{ fontWeight: 300 }}>
					{props.isSanitized ? values : values.join(", ")}
				</Typography>
			</td>
		</tr>
	);
};

const EligibilityConditions = (props: { agreement: GetRefundSourceConditionsQuery["baseAgreements"][number] }): any => {
	const { t } = useTranslation();

	if (!props.agreement || !props.agreement.eligibilityConditionSetsConnection?.edges?.length) return null;

	return props.agreement.eligibilityConditionSetsConnection.edges.map((condition, i) => {
		if (condition.node.code === EligibilityConditionCode.NUMBER_OF_PRESCRIPTIONS) {
			const prop = props.agreement.eligibilityConditionSetsConnection.edges.find(
				(condition) => condition.numberOfAdministeredRangeDays
			);
			const numberOfAdministeredRangeDays = prop?.numberOfAdministeredRangeDays || [0, 0];

			return (
				<Condition
					key={i}
					condition={t("NUMBER_OF_PRESCRIPTIONS")}
					values={[
						i18next.t(`NUMBER_OF_PRESCRIPTIONS_VALUE`, {
							numberOf: t("number_of"),
							treatments: props.agreement.eligibilityConditionTreatments
								?.map((t) => ` ${getTreatmentCodeByCountry(t as Treatment)} - ${t?.Name} `)
								.filter(Boolean),
							dispensedRange: t("dispensed_within_the_fixed_range_of"),
							dispensedRangeValue: `${numberOfAdministeredRangeDays[0]} - ${numberOfAdministeredRangeDays[1]}`,
							days: t(`days`),
							startingFromDisDate: t("starting_from_first_dispensing_date"),
							operator: t(prop?.numberOfAdministeredTreatmentsOperator || ""),
							numberOfAdministeredTreatments: prop?.numberOfAdministeredTreatments,
						}),
					]}
				/>
			);
		}

		if (condition.node.code === EligibilityConditionCode.TEST_THRESHOLD) {
			const prop = props.agreement.eligibilityConditionSetsConnection.edges.find((condition) => condition.referenceTestRangeDays);
			const referenceTestRangeDays = prop?.referenceTestRangeDays || [0, 0];

			return (
				<Condition
					key={i}
					condition={t("TEST_THRESHOLD")}
					values={[
						i18next.t("TEST_THRESHOLD_VALUE", {
							dateOfTestNumber: t("dateOfTestNumber"),
							testNumber: prop?.previousTestNumber,
							isWithin: t("is_within"),
							rangeDays: `${referenceTestRangeDays[0]} - ${referenceTestRangeDays[1]}`,
							daysAfter: t("days_after_dispensing_number"),
							referenceAdministrationSequenceNumber: prop?.referenceAdministrationSequenceNumber,
						}),
					]}
				/>
			);
		}

		if (condition.node.code === EligibilityConditionCode.ADMINISTRATION_ADHERENCE_RATE) {
			const prop = props.agreement.eligibilityConditionSetsConnection.edges.find((condition) => condition.minimumPercentage);

			return (
				<Condition
					key={i}
					condition={t("ADMINISTRATION_ADHERENCE_RATE")}
					values={[
						i18next.t("ADMINISTRATION_ADHERENCE_RATE_VALUE", {
							minimum: t("minimum"),
							minimumPercentage: `${prop?.minimumPercentage}%`,
							dispensingGap: t("for_duration_of_treatment_up_to"),
							gap: `${prop?.days}`,
							days: t("days"),
						}),
					]}
				/>
			);
		}

		return <></>;
	});
};

export const Conditions = (props: {
	agreementVersions: GetRefundSourceConditionsQuery["baseAgreements"];
	caseInstance?: GetRefundSourceConditionsQuery["cases"][number];
	snapshotObject: FlexFinancialDataPointSnapshot;
	dataContainers: GetRefundSourceConditionsQuery["baseAgreements"][number]["dataContainers"];
}) => {
	const { agreementVersions, caseInstance, snapshotObject } = props;
	const agreement = agreementVersions.find((v) => v.id === snapshotObject.agreementVersionId)!;
	const { t } = useTranslation();
	const certainConditions = snapshotObject.conditions.filter((condition) => condition.certainty === "certain");
	const agreementConditions = distinctBy(
		certainConditions.map((c) => ({
			...formatICondition(
				agreement.scenarios[c.scenarioOrder].conditions[c.conditionOrder] as any as ScenarioCondition,
				t,
				props.dataContainers,
				agreement.currency
			),
		})),
		(condition) => condition.name + condition.value
	);
	const cycleAdherenceCond = agreement.eligibilityConditionSetsConnection.edges.find(
		(f) => f.node.code === EligibilityConditionCode.CYCLE_ADHERENCE
	);

	return (
		<table style={{ tableLayout: "fixed" }}>
			<tbody>
				{agreement.coveringFormWaitInterval ? (
					<Condition condition={t("days-to-cover-prescription")} values={[agreement.coveringFormWaitInterval]} />
				) : (
					<></>
				)}
				{agreement.allowedFormFlexEntityTypes?.length ? (
					<Condition
						condition={t("allowedForms")}
						values={agreement.allowedFormFlexEntityTypes?.map((e) => t(e.localizationKey))}
					/>
				) : (
					<></>
				)}
				{agreement.allowedRecordFlexEntityTypes && agreement.requiredRecordFlexEntityTypes ? (
					<Condition
						condition={t("allowedRecords")}
						values={[...agreement.allowedRecordFlexEntityTypes, ...agreement.requiredRecordFlexEntityTypes].map((e) =>
							t(e.localizationKey)
						)}
					/>
				) : (
					<></>
				)}
				<Condition condition={t("preparations")} values={getPreparations(agreement as any)?.map((p) => p?.BrandName)} />
				<Condition
					condition={t("diseases")}
					values={caseInstance && caseInstance.disease ? [formatDisease(caseInstance.disease)] : [t(notAvailableKey)]}
				/>
				<Condition
					condition={t("indications")}
					values={
						caseInstance && caseInstance.indication ? [indicationDisplay(caseInstance.indication, t)] : [t(notAvailableKey)]
					}
				/>
				{cycleAdherenceCond && <Condition condition={t("cycle-adherence")} values={[t(formatEligibility(cycleAdherenceCond))]} />}
				{agreementConditions.length > 0 &&
					agreementConditions.map((c, i) => (
						<Condition key={i} condition={t(c.name)} values={[resolveContentForTooltip(c.value)]} isSanitized />
					))}
				{snapshotObject.coverEndDate ? (
					<Condition
						condition={t("to-be-covered-before")}
						values={[format(parseDate(snapshotObject.coverEndDate), "dd.MM.yyyy")]}
					/>
				) : (
					<></>
				)}
				<EligibilityConditions agreement={props.agreementVersions[0]} />
			</tbody>
		</table>
	);
};

export interface IFinancialDataPoint {
	evaluationSnapshot: string | null;
}

export interface IRefundRelatedConditions {
	agreementId: string;
	financialDataPoint?: IFinancialDataPoint;
	caseInstance?: GetReviewInvoiceQuery["documents"][number]["documentRefundGroups"][number]["case"];
	caseId?: string;
	evaluationSnapshot?: string;
}

function RefundRelatedConditions(props: IRefundRelatedConditions) {
	const { agreementId, financialDataPoint, caseInstance, caseId, evaluationSnapshot } = props;
	const [open, setOpen] = useState(false);
	const { palette } = useTheme();
	const { data, loading, error } = useQuery<GetRefundSourceConditionsQuery>(GET_REFUND_SOURCE_CONDITIONS, {
		skip: !agreementId || !open,
		variables: { agreementId: agreementId, caseId: caseId || caseInstance?.id },
	});

	if (error) {
		return <ErrorOutline />;
	}

	if (!agreementId || (!financialDataPoint && !evaluationSnapshot)) {
		return null;
	}

	function handleSetOpen() {
		setOpen(true);
	}

	const agreementVersions = data?.baseAgreements ?? [];
	const conditionSnapshot =
		tryDeserialize(evaluationSnapshot || financialDataPoint?.evaluationSnapshot || null, { conditions: [] }) || [];
	const caseDetails = data?.cases?.[0];

	return (
		<LiteBox display="flex" justifyContent="flex-start" borderLeft="5px solid transparent" width="100%" height="45px">
			<LiteBox display="flex" flexDirection="column" justifyContent="center">
				<ConditionsTooltip
					onOpen={handleSetOpen}
					title={
						loading || !agreementVersions.length || (!caseInstance && !caseId) || !caseDetails ? (
							<Loading />
						) : (
							<Conditions
								agreementVersions={agreementVersions}
								caseInstance={caseDetails}
								snapshotObject={conditionSnapshot}
								dataContainers={agreementVersions[0].dataContainers}
							/>
						)
					}
					placement="right"
				>
					<InfoOutlined htmlColor={palette.primary.main} />
				</ConditionsTooltip>
			</LiteBox>
		</LiteBox>
	);
}

export default memo(RefundRelatedConditions);
